import { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import store from 'store'
import { ApolloProvider, ApolloClient } from '@apollo/client'

import Spinner from 'components/spinner'
import ErrorComponent from 'components/error'
import ErrorMessage from 'components/errorMessage'
import { GlobalStyle } from 'css/utils/theme'
import { cache } from 'utils/cache'
import { link } from 'utils/apolloConfig'
import * as Sentry from '@sentry/react'
import { browserTracingIntegration } from '@sentry/react'
import { ToastContainer } from 'react-toastify'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || false

// we just initialize sentry when there is a valid DSN value
if (SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    integrations: [browserTracingIntegration()],
    // just captura 50% of data for performance monitoring
    tracesSampleRate: 0.5
  })
}

const Login = lazy(() => import('./login'))
const StackRoutes = lazy(() => import('routes'))

const client = new ApolloClient({
  cache,
  credentials: 'include',
  link
})

function PrivateRoute({ children }) {
  const hasToken = store.get('CORE_TOKEN')
  return hasToken ? children : <Navigate to="/login" replace />
}

const AppRoutes = () => {
  return (
    <ApolloProvider client={client}>
      <Sentry.ErrorBoundary fallback={ErrorComponent}>
        <BrowserRouter>
          <GlobalStyle />
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/error" element={<ErrorMessage />} />
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <StackRoutes />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </Sentry.ErrorBoundary>
      <ToastContainer position="top-center" />
    </ApolloProvider>
  )
}
const container = document.getElementById('app')
const root = createRoot(container!)
root.render(<AppRoutes />)
